import { useEffect, useState } from 'react';
import { Calendar, Instagram, Facebook, ExternalLink } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLanguage();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/consigli`)
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{lang === 'en' ? 'Our Tips' : 'I Nostri Consigli'} | Officina Martorana Bosch Car Service</title>
        <meta name="description" content={lang === 'en'
          ? 'Tips and advice from Officina Martorana Bosch Car Service.'
          : 'Consigli e suggerimenti da Officina Martorana Bosch Car Service.'} />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('blog', 'title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">{t('blog', 'subtitle')}</p>
          <div className="flex items-center justify-center space-x-4">
            <a href="https://www.instagram.com/officinamartoranacarservice" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity" data-testid="blog-instagram-link">
              <Instagram className="w-5 h-5" />
              <span className="font-medium">{t('blog', 'followInstagram')}</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=100063601aborede" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 bg-[#1877F2] text-white px-6 py-3 rounded-lg hover:bg-[#1664d9] transition-colors" data-testid="blog-facebook-link">
              <Facebook className="w-5 h-5" />
              <span className="font-medium">{t('blog', 'followFacebook')}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Consigli Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="consigli-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
              {lang === 'en' ? 'Our Tips' : 'I Nostri Consigli'}
            </h2>
            <a href="https://www.instagram.com/officinamartoranacarservice" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <Instagram className="w-7 h-7 text-[#E4405F]" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=100063601aborede" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <Facebook className="w-7 h-7 text-[#1877F2]" />
            </a>
          </div>

          {loading ? (
            <div className="text-center py-16" data-testid="blog-loading">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="blog-empty">
              <p>{lang === 'en' ? 'Tips coming soon...' : 'Consigli in arrivo...'}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, idx) => (
                <a
                  key={post.id}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-[#0066b2] overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2 block"
                  data-testid={`consiglio-card-${idx}`}
                >
                  {post.image_url && (
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.caption?.substring(0, 60) || 'Post'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      <div className="absolute top-3 right-3 bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                        <Instagram className="w-3 h-3" /> Instagram
                      </div>
                      {post.timestamp && (
                        <div className="absolute bottom-3 left-3 flex items-center gap-1 text-white/90 text-xs bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.timestamp)}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-5">
                    {post.caption && (
                      <p className="text-gray-600 text-sm line-clamp-4 leading-relaxed mb-3">{post.caption}</p>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-[#0066b2] text-sm font-medium">
                      <ExternalLink className="w-3.5 h-3.5" />
                      {lang === 'en' ? 'View on Instagram' : 'Guarda su Instagram'}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
